import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Table, Typography, message } from 'antd'
import { format, isBefore, setSeconds } from 'date-fns'
import {
  deleteDocument,
  getUnsignedDocs,
  updateDocument
} from '../../actions/admin'
import { setUploadState } from '../../actions'
import DocumentEditModal from "../util/DocumentEditModal"
import DocumentDeleteModal from "../util/DocumentDeleteModal"
import { isEndDateValid } from "../../utils/formValidation"
import EndDate from '../util/EndDate'
import DocumentActions from '../util/DocumentActions'
const { Title } = Typography
const { Column } = Table

const isExpired = (date) => {
  return date && isBefore(new Date(date), new Date())
}

const Admin = () => {
  const dispatch = useDispatch()
  const docs = useSelector(state => state.data.unsignedDocs)
  const uploadSuccess = useSelector(state => state.ui.upload.uploadSuccess)
  const uploadStatusCode = useSelector(state => state.ui.upload.uploadStatusCode)
  const [editModalVisible, setEditModalVisible] = useState(false)
  const [deleteModalVisible, setDeleteModalVisible] = useState(false)
  const [actionDocument, setActionDocument] = useState(null)
  const [actionLoading, setActionLoading] = useState(false)

  const handleEditActionOpen = (document) => {
    setActionDocument(document)
    setEditModalVisible(true)
  }

  const handleEditDocument = async (id, updatedFields) => {
    // Only enforce "not in the past" when the end date is actually being
    // changed. Otherwise an already-expired deal could never be edited for
    // anything else (ISIN, Løpenummer, allowed emails, ...) without also
    // pushing its expiration into the future.
    const originalTime = actionDocument && actionDocument.endDate ? new Date(actionDocument.endDate).getTime() : null
    const nextTime = updatedFields.endDate ? new Date(updatedFields.endDate).getTime() : null
    const endDateChanged = originalTime !== nextTime

    if (endDateChanged && !isEndDateValid(updatedFields.endDate)) {
      return
    }
    setActionLoading(true)
    dispatch(updateDocument(id, updatedFields))
  }

  const handleDisableSigning = async (document) => {
    // If the document IS expired, then set new endDate to null, else set
    // endDate to current date.
    dispatch(updateDocument(document._id, {
      endDate: !isExpired(document.endDate) ? setSeconds(new Date(), 0) : null
    }))
  }

  const handleDeleteActionOpen = async (document) => {
    setActionDocument(document)
    setDeleteModalVisible(true)
  }

  const handleDeleteDocument = async (id) => {
    setActionLoading(true)
    dispatch(deleteDocument(id))
  }

  useEffect(() => {
    dispatch(getUnsignedDocs())
  }, [dispatch])

  useEffect(() => {
    if (uploadSuccess !== null) {
      if (uploadSuccess) {
        setEditModalVisible(false)
        setActionDocument(null)
      } else {
        message.error('Error updating')
      }

      dispatch(setUploadState(null))
      setActionLoading(false)
    }
  }, [uploadSuccess, uploadStatusCode, dispatch])
  
  return (
    <div className="stdContent">
      <Title level={3}>Document list</Title>
      <Table dataSource={docs} bordered={true}>
        <Column title="Document Name" dataIndex="name" key="name" />
        <Column
          title="Uploaded by"
          dataIndex="authorEmail"
          key="authorEmail"
          render={(text, record) => record.authorEmail || '—'}
        />
        <Column
          title="Created Date"
          dataIndex="uploaded"
          key="uploaded"
          render={(text, record) => format(new Date(record.uploaded), 'MMMM do, yyyy H:mm')}
        />
        <Column
          title="End Date"
          dataIndex="endDate"
          key="endDate"
          render={(text, record) => <EndDate date={record.endDate}/> }
        />
        <Column
          title="Actions"
          key="actions"
          width={620}
          render={(text, record) => (
            <DocumentActions
              record={record}
              onEdit={handleEditActionOpen}
              onDisableSigning={handleDisableSigning}
              onDelete={handleDeleteActionOpen}
            />
          )}
        />
      </Table>
      
      <DocumentEditModal
        key={actionDocument ? actionDocument._id : 'empty'}
        visible={editModalVisible}
        document={actionDocument}
        onOk={(updatedFields) => handleEditDocument(actionDocument._id, updatedFields)}
        confirmLoading={actionLoading}
        onCancel={() => setEditModalVisible(false)}
      />

      <DocumentDeleteModal
        visible={deleteModalVisible}
        document={actionDocument}
        onOk={() => handleDeleteDocument(actionDocument._id)}
        confirmLoading={actionLoading}
        onCancel={() => setDeleteModalVisible(false)}  
      />
    </div>
  )
}

export default Admin
